import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import CheckCircleOutlined from "@ant-design/icons/CheckCircleOutlined";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import CloseCircleOutlined from "@ant-design/icons/CloseCircleOutlined";
import PauseCircleOutlined from "@ant-design/icons/PauseCircleOutlined";
import Tag from "antd/es/tag";
import { MarketplacePaymentDetails, PaymentDetails } from "@shared/types";
import { openMessage } from "utils/misc";
import { hexToRGB } from "@shared/utils/misc";
import usePaymentStatus from "@shared/features/settings/subscription/hooks/usePaymentStatus";
import { PaymentStatus } from "@shared/features/settings/subscription/types";
import styled from "styled-components";
import { Check, CircleX, Clock3, Pause } from "lucide-react";

interface PaymentStatusTagProps {
  paymentDetails: PaymentDetails | MarketplacePaymentDetails;
}

const PaymentStatusTag = ({ paymentDetails }: PaymentStatusTagProps) => {
  const { theme, lowerStatus, status, link, color, onClick, isClickable } =
    usePaymentStatus({
      paymentDetails,
      openMessage,
    });

  if (theme.isNewLook) {
    const NewLookIcon = NEW_LOOK_STATUS_TO_ICON[lowerStatus];
    const statusColor = theme.colors[color];
    return (
      <Link
        target="_blank"
        $isClickable={isClickable}
        href={isClickable ? link : undefined}
        onClick={onClick}
      >
        <NewLookStatus
          style={{
            color: statusColor,
            backgroundColor: hexToRGB(statusColor, 0.1),
          }}
        >
          {NewLookIcon && <NewLookIcon size={12} strokeWidth={2.25} />}
          {status}
        </NewLookStatus>
      </Link>
    );
  }

  return (
    <Link
      target="_blank"
      $isClickable={isClickable}
      href={isClickable ? link : undefined}
      onClick={onClick}
    >
      <Tag
        icon={STATUS_TO_ICON[lowerStatus]}
        style={{
          borderRadius: 4,
          backgroundColor: hexToRGB(theme.colors[color], 0.1),
          color: theme.colors[color],
          border: theme.borders.lightest,
          padding: "4px 8px",
        }}
      >
        {status}
      </Tag>
    </Link>
  );
};

export default PaymentStatusTag;

const STATUS_TO_ICON = {
  [PaymentStatus.PENDING]: <PauseCircleOutlined />,
  [PaymentStatus.CANCELLED]: <CloseCircleOutlined />,
  [PaymentStatus.PAID]: <CheckCircleOutlined />,
  [PaymentStatus.EXPIRED]: <ClockCircleOutlined />,
};

const Link = styled.a<{ $isClickable?: boolean }>`
  ${(p) => !p.$isClickable && "cursor: auto;"}
`;

const NEW_LOOK_STATUS_TO_ICON: Record<string, typeof Check | undefined> = {
  [PaymentStatus.PENDING]: Pause,
  [PaymentStatus.CANCELLED]: CircleX,
  [PaymentStatus.PAID]: Check,
  [PaymentStatus.EXPIRED]: Clock3,
};

const NewLookStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  /* The same chip as the new look's status Pill. */
  font-size: 11.5px;
  line-height: 16px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
`;
